import { deleteField, doc, serverTimestamp, setDoc, type DocumentData } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import { APP_FEATURE_KEYS, type AppFeatureKey } from '@/types'

/**
 * Reading and writing feature toggles (CROSS_PLATFORM_CONTRACTS.md §5).
 *
 * - Circle-wide `global_features/{businessId}` uses the field `enabled_features`
 *   (the shape iOS reads); `enabledFeatures` is accepted as a fallback.
 * - Per-user `user_features/{uid}` follows the general camelCase rule — Android
 *   writes `enabledFeatures` there — with `enabled_features` as the fallback.
 * - No document, or no list in it, means every feature is ON.
 * - A known key missing from a saved list is OFF; keys this panel doesn't know
 *   (added by a newer app) are kept untouched when saving.
 */
export type FeatureScope = 'global' | 'user'

interface ScopeConfig {
  collection: string
  field: string
  legacyField: string
  /** Extra fields written with every save. */
  stamp: (id: string, adminUid: string) => DocumentData
}

const SCOPES: Record<FeatureScope, ScopeConfig> = {
  global: {
    collection: 'global_features',
    field: 'enabled_features',
    legacyField: 'enabledFeatures',
    stamp: (id, adminUid) => ({ business_id: id, updated_at: serverTimestamp(), updated_by: adminUid }),
  },
  user: {
    collection: 'user_features',
    field: 'enabledFeatures',
    legacyField: 'enabled_features',
    stamp: (_id, adminUid) => ({ updatedAt: serverTimestamp(), updatedBy: adminUid }),
  },
}

export const featureCollection = (scope: FeatureScope) => SCOPES[scope].collection

/** The saved list, or `null` when nothing is saved (= everything ON). */
export function savedFeatureList(scope: FeatureScope, data: DocumentData | null | undefined): string[] | null {
  if (!data) return null
  const { field, legacyField } = SCOPES[scope]
  const list = data[field] ?? data[legacyField]
  return Array.isArray(list) ? list.filter((k): k is string => typeof k === 'string') : null
}

/** Which of the shared features are ON for a saved list. */
export function enabledKeys(saved: string[] | null): AppFeatureKey[] {
  if (saved === null) return [...APP_FEATURE_KEYS]
  return APP_FEATURE_KEYS.filter((key) => saved.includes(key))
}

/**
 * Save the ON features. Unknown keys from the previous list are carried over so a
 * newer app's features are not switched off by an older admin panel; the stale
 * spelling of the list field is removed so readers can't pick up an old copy.
 */
export async function saveFeatureList(
  scope: FeatureScope,
  id: string,
  enabled: AppFeatureKey[],
  previous: string[] | null,
  adminUid: string
): Promise<void> {
  const { collection, field, legacyField, stamp } = SCOPES[scope]
  const known = new Set<string>(APP_FEATURE_KEYS)
  const carried = (previous ?? []).filter((key) => !known.has(key))
  const list = [...APP_FEATURE_KEYS.filter((key) => enabled.includes(key)), ...carried]

  await setDoc(
    doc(db, collection, id),
    { [field]: list, [legacyField]: deleteField(), ...stamp(id, adminUid) },
    { merge: true }
  )
}
